import { Outlet, Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NavLink = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="text-sm tracking-wide text-[#2D2626] hover:text-[#6B1A24] transition-colors duration-300"
  >
    {children}
  </Link>
);

function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    onClose();
    nav(`/shop?q=${encodeURIComponent(term)}`);
  };

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-50 bg-[#FAFAF7]/95 backdrop-blur-xl">
      <div className="max-w-3xl mx-auto px-6 pt-32">
        <div className="flex items-center justify-between mb-8">
          <div className="label-eyebrow">Search</div>
          <button onClick={onClose} data-testid="search-close" className="p-2 hover:text-[#6B1A24]"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <form onSubmit={submit} className="relative">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-[#6B1A24]" size={22} strokeWidth={1.5} />
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search kurtis, corsets, motifs…"
            className="w-full bg-transparent pl-10 py-4 text-2xl md:text-3xl font-display border-b border-[#2D2626] focus:outline-none"
          />
        </form>
        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          {["chikankari", "banarasi", "maroon", "co-ord", "saffron"].map(s => (
            <button
              key={s}
              onClick={() => { onClose(); nav(`/shop?q=${encodeURIComponent(s)}`); }}
              data-testid={`search-suggest-${s}`}
              className="px-3 py-2 border border-[#E5DFD3] hover:border-[#6B1A24] hover:text-[#6B1A24] uppercase tracking-[0.2em]"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF7] text-[#2D2626] font-body">
      {/* Announcement bar */}
      <div className="bg-[#6B1A24] text-[#FAFAF7] text-[11px] tracking-[0.25em] uppercase py-2 text-center">
        Complimentary shipping on orders over $150 · Handcrafted in India
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAFAF7]/85 border-b border-[#E5DFD3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button
            className="md:hidden p-2 -ml-2"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(o => !o)}
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <nav className="hidden md:flex items-center gap-10">
            <NavLink to="/shop" testId="nav-shop-all">Shop All</NavLink>
            <NavLink to="/shop/kurti" testId="nav-kurtis">Kurtis</NavLink>
            <NavLink to="/shop/co-ord" testId="nav-coord">Co-ord Sets</NavLink>
          </nav>

          <Link to="/" data-testid="nav-logo" className="absolute left-1/2 -translate-x-1/2 font-display text-xl md:text-2xl tracking-[0.18em] text-[#6B1A24] whitespace-nowrap">
            SHRINGAARIKHAA
          </Link>

          <div className="flex items-center gap-5">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="nav-search-btn"
              aria-label="Search"
              className="hover:text-[#6B1A24] transition-colors"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="hidden md:block text-xs uppercase tracking-[0.2em] text-[#6B1A24]">Admin</Link>
            )}
            {user ? (
              <div className="hidden md:flex items-center gap-4">
                <Link to="/orders" data-testid="nav-orders" className="text-sm hover:text-[#6B1A24] transition-colors">Orders</Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-logout"
                  className="text-sm hover:text-[#6B1A24] transition-colors"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden md:flex items-center gap-2 text-sm hover:text-[#6B1A24] transition-colors">
                <User size={18} strokeWidth={1.5} /> Account
              </Link>
            )}
            <Link to="/cart" data-testid="nav-cart" className="relative flex items-center gap-2 hover:text-[#6B1A24] transition-colors">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute -top-2 -right-3 bg-[#6B1A24] text-[#FAFAF7] text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-[#E5DFD3] bg-[#FAFAF7]">
            <div className="px-6 py-6 flex flex-col gap-5">
              <Link to="/shop" onClick={() => setOpen(false)} data-testid="mnav-shop">Shop All</Link>
              <Link to="/shop/kurti" onClick={() => setOpen(false)} data-testid="mnav-kurtis">Kurtis</Link>
              <Link to="/shop/co-ord" onClick={() => setOpen(false)} data-testid="mnav-coord">Co-ord Sets</Link>
              {user ? (
                <>
                  <Link to="/orders" onClick={() => setOpen(false)} data-testid="mnav-orders">Orders</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} data-testid="mnav-admin">Admin</Link>}
                  <button onClick={async () => { setOpen(false); await handleLogout(); }} data-testid="mnav-logout" className="text-left">Sign Out</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mnav-login">Account</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-[#E5DFD3] bg-[#F3EFEA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="font-display text-2xl tracking-[0.18em] text-[#6B1A24]">SHRINGAARIKHAA</div>
            <p className="mt-4 text-sm text-[#5C5454] leading-relaxed">
              Heritage Indian craft, reimagined. Hand-finished kurtis and modern co-ord sets from artisan workshops across the country.
            </p>
          </div>
          <div>
            <div className="label-eyebrow">Shop</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/shop/kurti" className="hover:text-[#6B1A24]">Kurtis</Link></li>
              <li><Link to="/shop/co-ord" className="hover:text-[#6B1A24]">Co-ord Sets</Link></li>
              <li><Link to="/shop" className="hover:text-[#6B1A24]">All Collections</Link></li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Help</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>Shipping & Returns</li>
              <li>Size Guide</li>
              <li>Contact</li>
            </ul>
          </div>
          <div>
            <div className="label-eyebrow">Newsletter</div>
            <p className="mt-4 text-sm text-[#5C5454]">Get early access to new collections & artisan stories.</p>
            <form className="mt-4 flex border-b border-[#2D2626]" onSubmit={(e)=>e.preventDefault()}>
              <input data-testid="footer-newsletter-input" type="email" placeholder="your@email.com" className="flex-1 bg-transparent py-2 text-sm focus:outline-none" />
              <button data-testid="footer-newsletter-submit" className="text-xs uppercase tracking-[0.2em] text-[#6B1A24]">Join</button>
            </form>
          </div>
        </div>
        <div className="border-t border-[#E5DFD3] py-6 text-center text-xs uppercase tracking-[0.2em] text-[#5C5454]">
          © {new Date().getFullYear()} Shringaarikhaa · Made with care
        </div>
      </footer>
    </div>
  );
}
